import React, { useEffect } from 'react'
import { Head, router } from '@inertiajs/react'
import { AlertTriangle, Info, ShieldCheck, Wrench, Zap } from 'lucide-react'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import ComposioAppIcon from '@/frontend/components/Integrations/ComposioAppIcon'
import ComposioToolkitDetails from '@/frontend/components/Integrations/ComposioToolkitDetails'
import ComposioWorkersTable from '@/frontend/components/Integrations/ComposioWorkersTable'
import { ConnectionStatusChip } from '@/frontend/components/Integrations/ConnectionStatusChip'
import { BackLink } from '@/frontend/components/BackLink'
import PostConnectionModal from '@/frontend/components/PostConnectionModal'
import { usePermissions } from '@/frontend/hooks/usePermissions'
import { useJustConnectedWorker } from '@/frontend/hooks/useJustConnectedWorker'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import type { ComposioToolkit, ToolkitAction, WorkerConnectionStatus } from './types'

interface Props {
  page_title: string
  toolkit: ComposioToolkit
  toolkit_tools: ToolkitAction[]
  toolkit_triggers: ToolkitAction[]
  worker_statuses: WorkerConnectionStatus[]
  any_connected: boolean
  composio_just_connected_worker_id?: string | null
  composio_just_connected_key?: string | null
}

const AUTH_SCHEME_BADGE_VARIANTS: Record<string, React.ComponentProps<typeof Badge>['variant']> = {
  API_KEY: 'purple',
  OAUTH2: 'new',
  BEARER_TOKEN: 'success',
}

function normalizeAuthScheme(rawScheme: string | null | undefined) {
  const scheme = rawScheme?.trim()
  if (!scheme) return null

  const normalized = scheme.toUpperCase().replace(/[\s-]+/g, '_')
  switch (normalized) {
    case 'OAUTH_2_0':
    case 'OAUTH_2':
      return 'OAUTH2'
    case 'APIKEY':
      return 'API_KEY'
    default:
      return normalized
  }
}

const ComposioShow = ({ page_title, toolkit, toolkit_tools, toolkit_triggers, worker_statuses, any_connected, composio_just_connected_worker_id, composio_just_connected_key }: Props) => {
  const { can_manage_resources } = usePermissions()
  const connectable = toolkit.auth_mode === 'managed' || toolkit.auth_mode === 'custom'
  const authManagementLabel = {
    managed: <>Integração gerenciada pelo <span className="font-semibold text-foreground">Composio</span></>,
    custom: <>Autenticação customizada via <span className="font-semibold text-foreground">Composio</span></>,
    unsupported: <>Configuração manual necessária</>,
    none: <>Não requer autenticação</>,
  }[toolkit.auth_mode]
  const authSchemes = Array.from(
    new Set(
      [...toolkit.auth_schemes, toolkit.primary_auth_scheme]
        .map((scheme) => normalizeAuthScheme(scheme))
        .filter((scheme): scheme is string => Boolean(scheme))
    )
  )

  useEffect(() => {
    // Inclui os dois sinais pós-conexão no partial reload para receber o
    // payload deixado pelo callback do OAuth (que acontece em outra aba).
    const onFocus = () =>
      router.reload({
        only: [
          'worker_statuses',
          'any_connected',
          'composio_just_connected_worker_id',
          'composio_just_connected_key',
        ],
      })
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [])

  // Modal pós-conexão é alimentado pelo hook com signal explícito
  // (Composio usa page props dedicadas em vez do shared flash porque o OAuth
  // roda em outra aba e o flash do callback não alcança a aba original).
  const justConnected = useJustConnectedWorker(worker_statuses, {
    workerId: composio_just_connected_worker_id,
    key: composio_just_connected_key,
  })

  return (
    <>
      <Head title={page_title} />
      <div className="px-4 py-6 space-y-6">
        {/* Cabeçalho no padrão de detalhe do portal: breadcrumb fantasma +
            quadrado do ícone + nome 19/650 + chip de status via tokens (nunca
            verde cru). O título central duplicado e o "brilho" gradiente saíram. */}
        <div>
          <BackLink label="Integrações" fallbackHref="/dashboard/integrations" className="mb-2" />
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-secondary [&_svg]:size-6">
              <ComposioAppIcon logo={toolkit.logo} name={toolkit.name} className="size-6" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="truncate text-[19px] font-semibold tracking-[-0.01em] text-foreground" title={page_title}>
                  {page_title}
                </h1>
                <ConnectionStatusChip connected={any_connected} connectedLabel="Ativo" disconnectedLabel="Inativo" />
              </div>
            </div>
          </div>
        </div>

        {!can_manage_resources && (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Você não tem permissão para gerenciar integrações.</AlertTitle>
            <AlertDescription>Você pode visualizar as integrações conectadas, mas não pode modificá-las.</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardContent className="p-6">
            {(toolkit.long_description || toolkit.description) && (
              <p className="text-[13px] text-muted-foreground mb-5">{toolkit.long_description || toolkit.description}</p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              {toolkit_tools.length > 0 && (
                <Badge variant="secondary">
                  <Wrench />
                  {toolkit_tools.length} ferramentas
                </Badge>
              )}
              {toolkit_triggers.length > 0 && (
                <Badge variant="secondary">
                  <Zap />
                  {toolkit_triggers.length} gatilhos
                </Badge>
              )}
              {authSchemes.map((scheme) => (
                <Badge key={scheme} variant={AUTH_SCHEME_BADGE_VARIANTS[scheme] ?? 'secondary'}>
                  {scheme}
                </Badge>
              ))}
              <span className="inline-flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
                {authManagementLabel}
              </span>
            </div>

            {toolkit.no_auth && (
              <div className="flex items-center gap-2 mt-4 p-3 rounded-lg bg-muted text-[13px] text-muted-foreground">
                <Info className="h-4 w-4 shrink-0" />
                Esta integração não requer autenticação.
              </div>
            )}

            <div className="mt-6 border-t border-border pt-5">
              <ComposioWorkersTable
                workers={worker_statuses}
                appSlug={toolkit.slug}
                canManage={can_manage_resources}
                connectable={connectable}
              />
            </div>
          </CardContent>
        </Card>

        {(toolkit_tools.length > 0 || toolkit_triggers.length > 0) && (
          <ComposioToolkitDetails tools={toolkit_tools} triggers={toolkit_triggers} />
        )}
      </div>

      {justConnected.worker && (
        <PostConnectionModal
          open={justConnected.open}
          onClose={justConnected.close}
          worker={justConnected.worker}
          context={toolkit.name}
          connectionOnly
        />
      )}
    </>
  )
}

ComposioShow.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>

export default ComposioShow
